import React, { useState, useEffect } from "react";
import { Form, Input, Button, message, Typography, Flex, Divider, Radio } from "antd";
import { 
  RightCircleOutlined, 
  MailOutlined, 
  LockOutlined, 
  SafetyOutlined, 
  SecurityScanOutlined,
  ArrowRightOutlined,
  CheckOutlined
} from "@ant-design/icons";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import ReCAPTCHA from "react-google-recaptcha";
import video from "../../assets/gif/emailVerification.mp4";
import { Link } from "react-router-dom";
import axiosconfig from "../../config/axiosConfig";
import { handleBeforeInput } from "../../utils/validationFunctions";
import { changevalidations } from "../../utils/formValidations";
import "../../pages/auth/whatsapp-auth.css"; // Import WhatsApp themed CSS

const { Title, Text } = Typography;

// No longer needed as we are using custom captcha

const ForgotPassword = ({ handleToggleForgotPassword }) => {
  const [form] = Form.useForm();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [step1Data, setStep1Data] = useState(null);
  const [step2Data, setStep2Data] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpSentVia, setOtpSentVia] = useState("email");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [contactMethod, setContactMethod] = useState("phone");
  
  // Google reCAPTCHA state
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaError, setCaptchaError] = useState("");
  const recaptchaRef = React.useRef(null);

  // Handle reCAPTCHA verification
  const handleCaptchaChange = (token) => {
    setCaptchaVerified(!!token);
    setCaptchaError("");
  };

  // Handle reCAPTCHA expiry
  const handleCaptchaExpired = () => {
    setCaptchaVerified(false);
  };

  // Handle contact method change (email or phone)
  const handleContactMethodChange = (e) => {
    const method = e.target.value;
    setContactMethod(method);

    // Clear the other field when switching
    if (method === "phone") {
      form.setFieldsValue({ email: "" });
    } else {
      setPhoneNumber("");
      form.setFieldsValue({ mobile: "", countrycode: "" });
    }

    // Also clear validation errors
    form.validateFields(method === "phone" ? ["mobile"] : ["email"]);
  };

  // Handle phone input change
  const handlePhoneChange = (value, country) => {
    const dialCode = country.dialCode;
    setPhoneNumber(value);
    const code = `+${country.dialCode}`;
    setCountryCode(code);

    form.setFieldsValue({
      mobile: value.replace(code, ""),
      countrycode: code,
    });
  };

  // Handle country code field change (for direct editing)
  const handleCountryCodeChange = (e) => {
    const value = e.target.value;
    setCountryCode(value.startsWith("+") ? value : `+${value}`);
    form.setFieldsValue({
      countrycode: value.startsWith("+") ? value : `+${value}`,
    });
  };

  const handleSendOtp = async (values) => {
    // Verify reCAPTCHA first
    if (!captchaVerified) {
      setCaptchaError("Please complete the reCAPTCHA verification");
      return;
    }

    // Create the request body based on contact method
    const requestBody = {};

    // Add either email or mobile+countrycode based on contact method
    if (contactMethod === "email") {
      requestBody.email = values.email;
    } else {
      requestBody.mobile = values.mobile;
      requestBody.countrycode = values.countrycode.startsWith("+")
        ? values.countrycode
        : `+${values.countrycode}`;
    }

    console.log("Send OTP Request:", requestBody);
    await sendForgotPasswordOTP(requestBody);
  };

  const sendForgotPasswordOTP = (values) => {
    const isPhone = contactMethod === "phone";
    const isEmail = contactMethod === "email";
    let reqbody = {};
    
    if (isPhone) {
      const moblilewithccp = values.mobile;
      const localNumber = moblilewithccp.startsWith(
        countryCode.replace("+", "")
      )
        ? moblilewithccp.slice(countryCode.replace("+", "").length)
        : moblilewithccp;

      reqbody = {
        mobile: localNumber,
        countrycode: values.countrycode.replace("+", ""),
      };
    } else if (isEmail) {
      reqbody = {
        email: values.email,
      };
    }

    setLoading(true);
    axiosconfig
      .post("/company/forgetpasssendotp", reqbody) // API1 for sending forgot password OTP
      .then((response) => {
        console.log("response=>", response.data);
        if (response.data?.statuscode == 200) {
          const msg = response.data?.entity?.otpreponse?.otpsentvia;
          // Set the OTP delivery method based on the response
          if (msg) {
            setOtpSentVia(msg);
          } else {
            // Default to email if not specified
            setOtpSentVia("");
          }

          message.success(response.data.message);
          setStep(2);
          form.resetFields();
          setStep1Data(reqbody);
          setResendCooldown(30); // Start cooldown
        } else {
          message.error(
            response.data.message || "Failed to send OTP. Please try again."
          );
          // Reset reCAPTCHA on failure
          setCaptchaVerified(false);
          recaptchaRef.current?.reset();
        }
      })
      .catch((error) => {
        console.error("Forgot password error:", error);
        message.error("An error occurred. Please try again.");
        // Reset reCAPTCHA on failure
        setCaptchaVerified(false);
        recaptchaRef.current?.reset();
      })
      .finally(() => setLoading(false));
  };

  const handleOtpSubmit = (values) => {
    console.log(step1Data);

    // taking values from step 1 form - add only what was entered
    if (step1Data.email) {
      values.email = step1Data.email;
    }
    if (step1Data.mobile) {
      values.mobile = step1Data.mobile;
      values.countrycode = step1Data.countrycode;
    }
    
    setLoading(true);
    axiosconfig
      .post("/company/verifyforgetotp", values) // API2 for verifying OTP
      .then((response) => {
        console.log(response.data);
        if (response.data?.statuscode == 200) {
          message.success(response.data.message);
          form.resetFields();
          // redirect user to step 3
          setStep(3);
          setStep2Data(values);
        } else {
          message.error(
            response.data.message || "OTP verification failed. Please try again."
          );
        }
      })
      .catch((error) => {
        console.error("OTP verification error:", error);
        message.error("An error occurred during OTP verification. Please try again.");
      })
      .finally(() => setLoading(false));
  };

  const handlePasswordSubmit = (values) => {
    // Create clean request body with only required fields
    const requestBody = {
      otp: step2Data.otp,
      password: values.newpassword
    };
    
    // Add either email or mobile+countrycode based on step1Data
    if (step1Data.email) {
      requestBody.email = step1Data.email;
    }
    if (step1Data.mobile) {
      requestBody.mobile = step1Data.mobile;
      requestBody.countrycode = step1Data.countrycode;
    }
    
    console.log("Final Password Reset Data:", requestBody);
    setLoading(true);
    axiosconfig
      .post("/company/resetpassword", requestBody) // API3 for password reset
      .then((response) => {
        console.log("response=>", response.data); 
        if (response.data?.statuscode == 200) {
          message.success(response.data.message);
          form.resetFields();
          // Return to login after successful password reset
          setTimeout(() => {
            handlereset();
          }, 1500);
        } else {  
          message.error(
            response.data.message || "Password reset failed. Please try again."
          );
        }
      })
      .catch((error) => {
        console.error("Password reset error:", error);
        message.error("An error occurred during password reset. Please try again.");  
      })
      .finally(() => setLoading(false));
  };

  const handleResendOtp = () => {
    // Just call performResendOtp directly, no captcha
    performResendOtp();
  };

  // The actual resend OTP function that will be called
  const performResendOtp = () => {
    let reqbody = {};
    // Add only what was originally entered
    if (step1Data.email) {
      reqbody.email = step1Data.email;
    }
    if (step1Data.mobile) {
      reqbody.mobile = step1Data.mobile;
      reqbody.countrycode = step1Data.countrycode;
    }
    console.log(reqbody);

    setResendLoading(true);
    setLoading(true);
    axiosconfig
      .post("/company/forgetpasssendotp", reqbody) // Same API1 for resending OTP
      .then((response) => {
        console.log("response=>", response.data);
        if (response.data?.statuscode == 200) {
          // Update delivery method if it changed with the resend
          const newOtpSentVia = response.data.entity?.otpreponse?.otpsentvia;
          if (newOtpSentVia) {
            setOtpSentVia(newOtpSentVia);
          }

          // Clear the OTP field when new OTP is sent successfully
          form.setFieldValue("otp", "");

          message.success(
            `OTP resent to your ${newOtpSentVia}`
          );
          setResendCooldown(30); // Reset cooldown
        } else {
          // Clear OTP field on error as well
          form.setFieldValue("otp", "");
          message.error(
            response.data.message || "Failed to resend OTP. Please try again."
          );
        }
      })
      .catch((error) => {
        console.error("Resend OTP error:", error);
        // Clear OTP field on error as well
        form.setFieldValue("otp", "");
        message.error("An error occurred while resending OTP. Please try again.");
      })
      .finally(() => {
        setResendLoading(false);
        setLoading(false);
      });
  };
// console.log(otpSentVia,1);

  const handlereset = () => {
    handleToggleForgotPassword();
    setStep(1);
    setStep1Data(null);
    setStep2Data(null);
    setLoading(false);
    setContactMethod("phone");
    setPhoneNumber("");
    setCountryCode("");
    setCaptchaVerified(false);
    setCaptchaError("");
    recaptchaRef.current?.reset();
    form.resetFields();
  };

  // Cooldown timer effect
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  return (
    <>
      <div className="wa-auth-header">
        <Title level={2} className="wa-auth-title">
          {step === 1
            ? "Forgot Password"
            : step === 2
            ? "Verify OTP"
            : "Set New Password"}
        </Title>
        
        {step === 1 ? (
          <Text className="wa-auth-subtitle">
            Enter your email or mobile number to receive an OTP
          </Text>
        ) : step === 2 ? (
          <div style={{ textAlign: 'center', marginTop: '8px' }}>
            <Text strong style={{ fontSize: '15px', display: 'block', marginBottom: '4px', color: '#333333' }}>
              Enter the 6-digit OTP
            </Text>
            <Flex align="center" justify="center" gap="small">
              {otpSentVia?.toLowerCase() === "whatsapp" ? (
                <>
                  <span role="img" aria-label="whatsapp" style={{ fontSize: '16px' }}>📱</span>{" "}
                  <Text type="secondary" style={{ color: '#6B7280' }}>
                    Sent to your{" "}
                    <Text strong style={{ color: '#25D366' }}>
                      WhatsApp
                    </Text>
                  </Text>
                </>
              ) : otpSentVia?.toLowerCase() === "sms" ? (
                <>
                  💬
                  <Text type="secondary" style={{ color: '#6B7280' }}>
                    Sent to your{" "}
                    <Text strong style={{ color: '#25D366' }}>
                      phone via SMS
                    </Text>
                    <span role="img" aria-label="sms" style={{ fontSize: '16px' }}></span>{" "}
                  </Text>
                </>
              ) : (
                <>
                  <MailOutlined style={{ color: '#25D366' }} />{" "}
                  <Text type="secondary" style={{ color: '#6B7280' }}>
                    Sent to your{" "}
                    <Text strong style={{ color: '#25D366' }}>
                      Email
                    </Text>
                  </Text>
                </>
              )}
            </Flex>
          </div>
        ) : (
          <Text className="wa-auth-subtitle">Set your new password</Text>
        )}
      </div>
      
      <Form 
        form={form} 
        onFinish={
          step === 1
            ? handleSendOtp
            : step === 2
            ? handleOtpSubmit
            : handlePasswordSubmit
        } 
        layout="vertical"
        disabled={loading}
      >
        {step === 1 && (
          <>
            {/* Contact method selector */}
            <Form.Item>
              <Radio.Group
                onChange={handleContactMethodChange}
                value={contactMethod}
              >
                <Radio value="phone">Mobile</Radio>
                <Radio value="email">Email</Radio>
              </Radio.Group>
            </Form.Item>

            {/* Conditionally show either mobile or email input based on selection */}
            {contactMethod === "phone" ? (
              <Form.Item
                name="mobile"
                label="Mobile Number"
                rules={[
                  { required: true, message: "Please enter a mobile number" },
                ]}
              >
                <PhoneInput
                  country={"in"}
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  inputClass="wa-input"
                  containerClass="wa-phone-input"
                  dropdownClass="phone-dropdown"
                  enableSearch={true}
                  disableSearchIcon={true}
                  searchPlaceholder="Search country..."
                  countryCodeEditable={false}
                />
              </Form.Item>
            ) : (
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: "Please enter your email" },
                  { type: "email", message: "Invalid email" },
                ]}
              >
                <Input
                  prefix={<MailOutlined className="wa-input-prefix" />}
                  placeholder="Enter Email"
                  className="wa-input"
                  onBeforeInput={handleBeforeInput(
                    changevalidations.allwithoutspace,
                    30
                  )}
                />
              </Form.Item>
            )}

            {/* Hidden field to store country code */}
            <Form.Item name="countrycode" hidden initialValue="+91" />

            <Form.Item
              className="wa-captcha-wrapper"
            >
              <div className="wa-recaptcha-wrap">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={handleCaptchaChange}
                  onExpired={handleCaptchaExpired}
                  size="normal"
                />
              </div>
              {captchaError && (
                <Text type="danger" style={{ display: 'block', textAlign: 'center', marginTop: 4 }}>{captchaError}</Text>
              )}
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                icon={<ArrowRightOutlined />}
                className="wa-auth-button"
              >
                Send OTP
              </Button>
            </Form.Item>
          </>
        )}

        {step === 2 && (
          <>
            <Flex justify="center">
              <video
                src={video}
                alt="No Data Found"
                style={{ width: "100px", height: "100px" }}
                autoPlay
                loop
                className="mb-2"
              />
            </Flex>
            <Form.Item
              name="otp"
              label="OTP Code"
              rules={[
                { required: true, message: "Please enter the OTP" },
                { len: 6, message: "OTP must be 6 digits" },
              ]}
            >
              <Input
                prefix={<SafetyOutlined className="wa-input-prefix" />}
                placeholder="Enter 6-digit OTP"
                maxLength={6}
                className="wa-input"
                onBeforeInput={handleBeforeInput(
                  changevalidations.numberwithoutpoint,
                  6
                )}
              />
            </Form.Item>
            <Form.Item>
              <Button
                type="link"
                onClick={performResendOtp}
                disabled={resendLoading || resendCooldown > 0}
                loading={resendLoading}
                block
                style={{ padding: 0 }}
              >
                {resendCooldown > 0
                  ? `Resend OTP (${resendCooldown}s)`
                  : "Resend OTP"}
              </Button>
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="wa-auth-button"
                loading={loading}
                block
                icon={<ArrowRightOutlined />}
              >
                Next
              </Button>
            </Form.Item>
          </>
        )}

        {step === 3 && (
          <>
            <Form.Item
              name="newpassword"
              label="New Password"
              rules={[
                { required: true, message: "Please enter your new password" },
                { min: 6, message: "Password must be at least 6 characters" }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="wa-input-prefix" />}
                placeholder="Enter new password"
                className="wa-input"
              />
            </Form.Item>

            <Form.Item
              name="confirmpassword"
              label="Confirm Password"
              dependencies={["newpassword"]}
              rules={[
                { required: true, message: "Please confirm your password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("newpassword") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject("Passwords do not match!");
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="wa-input-prefix" />}
                placeholder="Confirm new password"
                className="wa-input"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                icon={<CheckOutlined />}
                className="wa-auth-button"
              >
                Reset Password
              </Button>
            </Form.Item>
          </>
        )}
      </Form>
      
      <div className="wa-auth-footer">
        {step === 1 ? (
          <Text style={{ color: '#6B7280' }}>
            Remember your password?{" "}
            <Button
              type="link"
              className="wa-auth-link"
              onClick={handlereset}
              style={{ padding: 0 }}
            >
              Log In
            </Button>
          </Text>
        ) : (
          <a
            onClick={() => {
              if(step === 2) {
                setStep(1);
              } else if(step === 3) {
                setStep(2);
              }
              // Restore form data when going back
              if (step1Data) {
                form.setFieldsValue({
                  email: step1Data.email,
                  mobile: step1Data.mobile,
                  countrycode: step1Data.countrycode,
                });
              }
            }}
            className="wa-auth-link"
            style={{ display: 'block', textAlign: 'center', marginTop: '20px' }}
          >
            ← Back
          </a>
        )}
      </div>
    </>
  );
};

export default ForgotPassword;
